/* PLATFORM COLORS
 * These replace BouColor on the platform.
 * Do not directly reference them; instead, reference customTheme.
 */

/* SECTION #1 – UPRIGHT PLATFORM COLOR SCALES
 * Scaled color variables follow the following format: $[color-name]_[number] | examples: GRAY_000, GRAY_100
 * The enum's are exported in order to suppress warnings, but do not use them directly. Use PlatformColor.
 */
export enum PlatformGrayScale {
  GRAY_000 = '#FAFBFC',
  GRAY_100 = '#F6F8FA',
  GRAY_200 = '#E1E4E8',
  GRAY_300 = '#D0D7DE',
  GRAY_400 = '#959DA5',
  GRAY_500 = '#6A737D',
  GRAY_600 = '#586069',
  GRAY_700 = '#444D56',
  GRAY_800 = '#2F363D',
  GRAY_900 = '#24292E',
  BLACK = '#1B1F23',
  WHITE = '#FFF',
}

export enum PlatformBlueScale {
  BLUE_000 = '#F1F8FF',
  BLUE_100 = '#DBEDFF',
  BLUE_200 = '#C8E1FF',
  BLUE_300 = '#79B8FF',
  BLUE_400 = '#2188FF',
  BLUE_500 = '#0366D6',
  BLUE_600 = '#005CC5',
  BLUE_700 = '#044289',
  BLUE_800 = '#032F62',
  BLUE_900 = '#05264C',
}

export enum PlatformGreenScale {
  GREEN_000 = '#F0FFF4',
  GREEN_100 = '#DCFFE4',
  GREEN_200 = '#BEF5CB',
  GREEN_300 = '#85E89D',
  GREEN_400 = '#34D058',
  GREEN_500 = '#28A745',
  GREEN_600 = '#22863A',
  GREEN_700 = '#176F2C',
  GREEN_800 = '#165C26',
  GREEN_900 = '#144620',
}

// TODO: make the whole scale adhere to Figma. Most of these were copied from other scales
// on the internet.
export enum PlatformTealScale {
  TEAL_000 = '#F0FFF9',
  TEAL_100 = '#E0FFFA',
  TEAL_200 = '#C7FFFA',
  TEAL_300 = '#99FFED',
  TEAL_400 = '#34D0B6',
  TEAL_500 = '#28A792',
  TEAL_600 = '#228675',
  TEAL_700 = '#176F60',
  TEAL_800 = '#165C50',
  TEAL_900 = '#00665C',
}

export enum PlatformYellowScale {
  YELLOW_000 = '#FFFDEF',
  YELLOW_100 = '#FFFBDD',
  YELLOW_200 = '#FFF5B1',
  YELLOW_300 = '#FFEA7F',
  YELLOW_400 = '#FFDF5D',
  YELLOW_500 = '#FFD33D',
  YELLOW_600 = '#F9C513',
  YELLOW_700 = '#DBAB09',
  YELLOW_800 = '#B08800',
  YELLOW_900 = '#735C0F',
}

export enum PlatformRedScale {
  RED_000 = '#FFEBE9',
  RED_100 = '#FFCECB',
  RED_200 = '#FFABA8',
  RED_300 = '#FF8182',
  RED_400 = '#FA4549',
  RED_500 = '#CF222E',
  RED_600 = '#A40E26',
  RED_700 = '#82071E',
  RED_800 = '#660018',
  RED_900 = '#4C0014',
}

export enum PlatformOrangeScale {
  ORANGE_000 = '#FFF8F2',
  ORANGE_100 = '#FFEBDA',
  ORANGE_200 = '#FFD1AC',
  ORANGE_300 = '#FFAB70',
  ORANGE_400 = '#FB8532',
  ORANGE_500 = '#F66A0A',
  ORANGE_600 = '#E36209',
  ORANGE_700 = '#D15704',
  ORANGE_800 = '#C24E00',
  ORANGE_900 = '#A04100',
}

export enum PlatformPinkScale {
  PINK_000 = '#FFEEF8',
  PINK_100 = '#FEDBF0',
  PINK_200 = '#F9B3DD',
  PINK_300 = '#F692CE',
  PINK_400 = '#EC6CB9',
  PINK_500 = '#EA4AAA',
  PINK_600 = '#D03592',
  PINK_700 = '#B93A86',
  PINK_800 = '#99306F',
  PINK_900 = '#6D224F',
}

export enum PlatformPurpleScale {
  PURPLE_000 = '#F5F0FF',
  PURPLE_100 = '#E6DCFD',
  PURPLE_200 = '#D1BCF9',
  PURPLE_300 = '#B392F0',
  PURPLE_400 = '#8A63D2',
  PURPLE_500 = '#6F42C1',
  PURPLE_600 = '#5A32A3',
  PURPLE_700 = '#4C2888',
  PURPLE_800 = '#3A1D6E',
  PURPLE_900 = '#29134E',
}

export enum PlatformImpactScale {
  POSITIVE_STRONG = '#42E7B4',
  POSITIVE_MODERATE = '#A2E6DA',
  POSITIVE_WEAK = '#E6F8F5',
  NEGATIVE_WEAK = '#F8F0FD',
  NEGATIVE_MODERATE = '#E7CEF9',
  NEGATIVE_STRONG = '#7400B8',
}

export enum PlatformImpactTextColors {
  POSITIVE_LIGHT = '#00FFC2',
  POSITIVE_DARK = '#2EB8A0',
  NEGATIVE_LIGHT = '#B433FF',
  NEGATIVE_DARK = '#7400B8',
}

/*
 * SECTION #2 – COLOR UTILITIES
 * Color utilities are based on foregrounds (FG) and backgrounds (BG)
 * Foregrounds are used for text and icons
 * Backgrounds are used for surfaces of components or UI elements, such as pages, boxes, and overlays
 * We translate foregrounds and backgrounds into component/color role tokens, so they're easy to reference (e.g. TextColor, BorderColor) – see section #3 below.
 */

// FOREGROUNDS
const FgColor = {
  // Foundations:
  FG_DEFAULT: PlatformGrayScale.BLACK,
  FG_GRAY_DARK: PlatformGrayScale.GRAY_900,
  FG_GRAY_DEFAULT: PlatformGrayScale.GRAY_600,
  FG_GRAY_LIGHT: PlatformGrayScale.GRAY_500,
  FG_WHITE: PlatformGrayScale.WHITE,
  // Color roles:
  FG_ACCENT: PlatformGrayScale.GRAY_900,
  FG_DISABLED: PlatformGrayScale.GRAY_400,
};

// BACKGROUNDS
const BgColor = {
  BG_WHITE: PlatformGrayScale.WHITE,
  BG_GRAY_LIGHTER: PlatformGrayScale.GRAY_000,
  // Light gray
  BG_GRAY_LIGHT: PlatformGrayScale.GRAY_100,
  BG_GRAY_LIGHT_HOVER: '#f3f4f6',
  // Default gray
  BG_GRAY_DEFAULT: PlatformGrayScale.GRAY_200,
  BG_GRAY_DEFAULT_HOVER: '#EBEDF0',
  BG_GRAY_DARK: PlatformGrayScale.GRAY_800,
  // Black
  BG_BLACK: PlatformGrayScale.BLACK,
  BG_BLACK_HOVER: PlatformGrayScale.GRAY_800,
  BG_BLACK_ACTIVE: PlatformGrayScale.GRAY_700,
} as const;

/*
 * SECTION #3 – COLOR ROLES
 * Functional roles e.g. by component, state, border.
 * NAMING INSTRUCTIONS: first the component (e.g. BUTTON), then possible variant (e.g. PRIMARY), then attribute (e.g. BG), then possible state (e.g. HOVER)
 */

const TextColor = {
  TEXT_BLACK: FgColor.FG_DEFAULT,
  TEXT_GRAY_DARK: FgColor.FG_GRAY_DARK,
  TEXT_GRAY: FgColor.FG_GRAY_DEFAULT,
  TEXT_GRAY_LIGHT: FgColor.FG_GRAY_LIGHT,
  TEXT_WHITE: FgColor.FG_WHITE,
  TEXT_DISABLED: FgColor.FG_DISABLED,
  TEXT_BLUE: PlatformBlueScale.BLUE_500,
} as const;

const IconColor = {
  ICON_GRAY_DARK: PlatformGrayScale.GRAY_800,
  ICON_GRAY_LIGHT: PlatformGrayScale.GRAY_500,
  ICON_GRAY_ON_DARK_BG: PlatformGrayScale.GRAY_400,
} as const;

const BorderColor = {
  BORDER_WHITE: PlatformGrayScale.WHITE,
  // Gray borders:
  BORDER_GRAY_LIGHT: PlatformGrayScale.GRAY_200,
  BORDER_GRAY_DEFAULT: PlatformGrayScale.GRAY_300,
  BORDER_GRAY_DEFAULT_ACTIVE: PlatformGrayScale.GRAY_400,
  BORDER_GRAY_DARK: PlatformGrayScale.GRAY_700,
  // Accent border:
  BORDER_ACCENT: PlatformGrayScale.GRAY_900, // This affects e.g. UnderlineNav. Alternative: PlatformYellowScale.YELLOW_600,
} as const;

const ButtonColor = {
  // Common:
  BUTTON_DISABLED_BG: BgColor.BG_GRAY_LIGHT,
  // Default:
  BUTTON_DEFAULT_BG: BgColor.BG_GRAY_LIGHT,
  BUTTON_DEFAULT_BG_HOVER: BgColor.BG_GRAY_LIGHT_HOVER,
  BUTTON_DEFAULT_BG_ACTIVE: BgColor.BG_BLACK_ACTIVE,
  BUTTON_DEFAULT_TEXT: TextColor.TEXT_GRAY_DARK,
  BUTTON_DEFAULT_BORDER: 'rgba(31, 35, 40, 0.15)',
  // Primary:
  BUTTON_PRIMARY_BG: BgColor.BG_BLACK,
  BUTTON_PRIMARY_BG_HOVER: BgColor.BG_BLACK_HOVER,
  BUTTON_PRIMARY_BG_ACTIVE: BgColor.BG_BLACK_ACTIVE,
  BUTTON_PRIMARY_TEXT: FgColor.FG_WHITE,
  BUTTON_PRIMARY_BORDER: 'rgba(31, 35, 40, 0.15)',
} as const;

const StateColor = {
  // Neutral
  STATE_NEUTRAL_FG: '#1D4ED8',
  STATE_NEUTRAL_EMPHASIS: '#60A5FA',
  STATE_NEUTRAL_MUTED: '#dae6f5',
  STATE_NEUTRAL_SUBTLE: '#EFF6FF',
  // Success. Use to emphasize a positive message.
  STATE_SUCCESS_FG: '#397C51',
  STATE_SUCCESS_EMPHASIS: '#22C55E',
  STATE_SUCCESS_MUTED: '#daf0e1',
  STATE_SUCCESS_SUBTLE: '#F0FDF4',
  // Attention. Use to highlight text or icons that require the user's attention.
  STATE_ATTENTION_FG: '#A16207',
  STATE_ATTENTION_EMPHASIS: '#FACC14',
  STATE_ATTENTION_MUTED: '#faf7d7',
  STATE_ATTENTION_SUBTLE: '#FEFCE8',
  // Danger. Use to emphasize an error or a blocking status. Action is required.
  STATE_DANGER_FG: '#B91C1C',
  STATE_DANGER_EMPHASIS: '#F87171',
  STATE_DANGER_MUTED: '#f7d7d7',
  STATE_DANGER_SUBTLE: '#FEF2F2',
} as const;

// Impact chart & other impact related
const ImpactColor = {
  IMPACT_POSITIVE_STRONG: PlatformImpactScale.POSITIVE_STRONG,
  IMPACT_POSITIVE_MODERATE: PlatformImpactScale.POSITIVE_MODERATE,
  IMPACT_POSITIVE_WEAK: PlatformImpactScale.POSITIVE_WEAK,
  IMPACT_NEGATIVE_WEAK: PlatformImpactScale.NEGATIVE_WEAK,
  IMPACT_NEGATIVE_MODERATE: PlatformImpactScale.NEGATIVE_MODERATE,
  IMPACT_NEGATIVE_STRONG: PlatformImpactScale.NEGATIVE_STRONG,

  // Added for CSRD UI refresh from Janne Aukia's design
  IMPACT_NEGATIVE_DARK: '#691EB1',
  IMPACT_POSITIVE_DARK: '#3F9B86',
} as const;

const SDGColor = {
  SDG_ALIGNED_STRONG_FG: PlatformGrayScale.WHITE,
  SDG_ALIGNED_MODERATE_FG: PlatformGreenScale.GREEN_700,
  SDG_ALIGNED_WEAK_FG: PlatformGreenScale.GREEN_900,
  SDG_MISALIGNED_STRONG_FG: PlatformGrayScale.WHITE,
  SDG_MISALIGNED_MODERATE_FG: PlatformPurpleScale.PURPLE_000,
  SDG_MISALIGNED_WEAK_FG: PlatformPurpleScale.PURPLE_900,
};

// Tooltips explaining NIR and NIS formulas
const ImpactTextColor = {
  IMPACT_TEXT_POSITIVE_LIGHT: PlatformImpactTextColors.POSITIVE_LIGHT,
  IMPACT_TEXT_POSITIVE_DARK: PlatformImpactTextColors.POSITIVE_DARK,
  IMPACT_TEXT_NEGATIVE_LIGHT: PlatformImpactTextColors.NEGATIVE_LIGHT,
  IMPACT_TEXT_NEGATIVE_DARK: PlatformImpactTextColors.NEGATIVE_DARK,
};

const ComparisonColor = {
  DEFAULT_COMPARISON: '#240038',
  BENCHMARK_COMPARISON: PlatformPinkScale.PINK_300,
};

export const PlatformColor = {
  ...FgColor,
  ...BgColor,
  ...TextColor,
  ...IconColor,
  ...ButtonColor,
  ...BorderColor,
  ...StateColor,
  ...ImpactColor,
  ...ImpactTextColor,
  ...SDGColor,
  ...ComparisonColor,
} as const;

export type PlatformColor = (typeof PlatformColor)[keyof typeof PlatformColor];
